import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { IoCheckmarkCircle, IoChevronForward, IoTimeOutline, IoEllipse } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import ReviewSpace from "@/components/ReviewSpace";
import { useAuth } from "@/context/AuthContext";
import { adminListReviews, adminMarkReviewSeen } from "@/lib/api";

const TABS = [
  { key: "non_lu", label: "Non lu", icon: <IoEllipse size={11} /> },
  { key: "en_cours", label: "En cours", icon: <IoTimeOutline size={13} /> },
  { key: "termine", label: "Terminé", icon: <IoCheckmarkCircle size={13} /> },
];
const STATUS_STYLE = {
  non_lu: { bg: "#fff3e0", fg: "#b26a00", label: "Non lu" },
  en_cours: { bg: "color-mix(in srgb, var(--brand) 14%, transparent)", fg: RED, label: "En cours" },
  termine: { bg: "#e8f7ec", fg: "#1a7f37", label: "Terminé" },
};

function ReviewRow({ r, onOpen }) {
  const st = STATUS_STYLE[r.status] || STATUS_STYLE.non_lu;
  const nb = (r.thread || []).length;
  return (
    <button style={sx.row} onClick={onOpen} data-testid={`review-row-${r.id}`}>
      {r.status === "non_lu" && <span style={sx.dot} data-testid="review-unread-dot" />}
      {r.member_avatar ? <img src={r.member_avatar} alt="" style={sx.avatar} /> : <span style={sx.avatarFb}>{(r.member_name || r.member_email || "?").slice(0, 1).toUpperCase()}</span>}
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <div style={sx.rowTitle}>{r.title}</div>
        <div style={sx.meta}>{r.member_name || r.member_email}{r.category_label ? ` · ${r.category_label}` : ""}{nb ? ` · ${nb} message${nb > 1 ? "s" : ""}` : ""}</div>
      </div>
      <span style={{ ...sx.status, background: st.bg, color: st.fg }}>{st.label}</span>
      <span style={sx.seeBtn} data-testid={`review-see-${r.id}`}>Voir la relecture <IoChevronForward size={14} /></span>
    </button>
  );
}

export default function AdminCarnets() {
  const { isAdmin, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [tab, setTab] = useState("non_lu");
  const [openId, setOpenId] = useState(params.get("open") || null);

  const load = () => adminListReviews().then(setList).catch(() => toast.error("Chargement impossible")).finally(() => setLoadingData(false));
  useEffect(() => { if (!loading && isAdmin === false) nav("/hub", { replace: true }); }, [loading, isAdmin, nav]);
  useEffect(() => { if (loading || !isAdmin) return; load(); /* eslint-disable-next-line */ }, [loading, isAdmin]);

  // Accès direct depuis une notification (?open=<id>) : ouvre et marque comme lu.
  useEffect(() => {
    const oid = params.get("open");
    if (oid && !loadingData && list.some((r) => r.id === oid)) {
      const r = list.find((x) => x.id === oid);
      setOpenId(oid);
      if (r && r.status === "non_lu") { adminMarkReviewSeen(oid).then(load).catch(() => {}); }
      params.delete("open"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [list, loadingData]);

  const counts = { non_lu: 0, en_cours: 0, termine: 0 };
  list.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
  const grouped = list.filter((r) => r.status === tab);
  const opened = list.find((r) => r.id === openId) || null;

  const openReview = async (r) => {
    setOpenId(r.id);
    if (r.status === "non_lu") { try { await adminMarkReviewSeen(r.id); } catch {} load(); }
  };
  useEffect(() => { if (openId && !loadingData && !list.some((r) => r.id === openId)) setOpenId(null); }, [list, openId, loadingData]);

  return (
    <DojoShell crumbs={[{ label: "Relectures" }]}>
      {opened ? (
        <ReviewSpace review={opened} role="sensei" onBack={() => setOpenId(null)} onChanged={load} />
      ) : (
        <>
          <h1 style={sx.h1}>Demandes de relecture</h1>
          <p style={sx.subh}>Chaque relecture s'ouvre dans son propre espace. Classe-les en un coup d'œil : à traiter, en cours, ou terminées.</p>

          <div style={sx.tabs} data-testid="review-tabs">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`review-tab-${t.key}`}
                style={{ ...sx.tab, ...(tab === t.key ? sx.tabActive : {}) }}>
                {t.icon} {t.label}
                <span style={{ ...sx.tabCount, ...(tab === t.key ? sx.tabCountActive : {}) }}>{counts[t.key] || 0}</span>
              </button>
            ))}
          </div>

          {loadingData ? <p style={{ color: "var(--text-secondary)" }}>Chargement…</p> : grouped.length === 0 ? (
            <div style={sx.empty} data-testid="reviews-empty">
              {tab === "non_lu" ? "Aucune relecture à traiter. 🎉" : tab === "en_cours" ? "Aucune relecture en cours." : "Aucune relecture terminée pour l'instant."}
            </div>
          ) : (
            <div style={sx.rows} data-testid="reviews-list">
              {grouped.map((r) => <ReviewRow key={r.id} r={r} onOpen={() => openReview(r)} />)}
            </div>
          )}
        </>
      )}
    </DojoShell>
  );
}

const sx = {
  h1: { fontSize: 30, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", margin: 0 },
  subh: { color: "var(--text-secondary)", fontSize: 14, margin: "6px 0 20px", maxWidth: 640 },
  tabs: { display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" },
  tab: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  tabActive: { background: RED, color: "#fff", border: `1.5px solid ${RED}` },
  tabCount: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-primary)", fontSize: 12, fontWeight: 900 },
  tabCountActive: { background: "rgba(255,255,255,0.25)", color: "#fff" },
  empty: { padding: "44px 20px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 16 },
  rows: { display: "flex", flexDirection: "column", gap: 10 },
  row: { position: "relative", display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px 16px", borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", cursor: "pointer" },
  dot: { position: "absolute", top: 12, left: 6, width: 8, height: 8, borderRadius: "50%", background: RED },
  avatar: { width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  avatarFb: { width: 44, height: 44, borderRadius: "50%", background: RED, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, flexShrink: 0 },
  rowTitle: { fontWeight: 800, color: "var(--text-primary)", fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  meta: { fontSize: 12.5, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  status: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, flexShrink: 0, whiteSpace: "nowrap" },
  seeBtn: { display: "inline-flex", alignItems: "center", gap: 4, color: RED, fontWeight: 800, fontSize: 13, flexShrink: 0 },
};
